/**
 * LinkRepairFixturePage — a dev-only harness for seeing the stale-register-link repair
 * (`register-link-repair`, epic `asset-register-and-library` feature 37, ledger row 83).
 *
 * ## Why a fixture page rather than the real wizard
 *
 * The state this feature exists for cannot be reached through the UI, and that is not an
 * oversight — it is two deliberate guards meeting:
 *
 *  - **The wizard loads the LATEST plan for a twin** (`usePlanLoad`'s plan-load effect sorts
 *    `summaries` by descending id and takes the first). AdminDB Staging's twin 532 `PUL2-COL` has
 *    eleven plans and the five real stale links live on plan **11**, while the latest is **32**.
 *    Plan 11 is therefore unreachable from `/onboarding`.
 *  - **A placed register row cannot be deleted**, so a fresh one cannot be manufactured either:
 *    `AssetRegisterList` refuses removal while the row is placed, and refuses again while
 *    placement is merely unknown.
 *
 * The remaining option would be writing to AdminDB Staging, which this feature's build is not
 * permitted to do. So the sanctioned route is the one the design-system gates already name: a
 * **dev-only fixture page**, driven by Playwright for the before/after A/B.
 *
 * ## What is on it, and where the data came from
 *
 * Every fixture below is the shape measured read-only on AdminDB Staging on 2026-09-06, not an
 * invention. Twin 532's register was cleared and re-imported on 2026-08-06 — 83 rows, ids
 * 9508-9590, all created in the same instant, none carrying an `entity_code` — while plan 11's
 * items still point at ids 9427-9476, a block that exists for no twin.
 *
 * The five real items therefore carry **no entity identity on either side**, which is exactly why
 * the automatic repair cannot help them and the properties panel's **Unlink** is their only route
 * back. A sixth, **synthetic**, item is added carrying an identity that names a free row, because
 * the automatic half has no live instance on either database and must still be seen working.
 *
 * ## It cannot reach production, and its data does not ship
 *
 * The route is registered in `App.tsx` only when `process.env.NODE_ENV !== 'production'`, which
 * webpack constant-folds away, and it is TL_ADMIN-gated and unlinked from any UI besides — the
 * same shape as the other dev-only routes. It performs no network call and no
 * write: both stores are seeded synchronously.
 *
 * ⚠ **The route going is not the same as the DATA going, and the code review measured the
 * difference.** A static import is unconditional, so the module stayed in the graph and this
 * file's seeded staging register data — the twin BID, the twin id, six row ids and the customer's
 * equipment names — shipped in the production bundle while three separate comments claimed it
 * could not. The fixture constants therefore live inside the component body, where Terser drops
 * them along with the component nothing references. Verified the way the problem was found:
 * build, then grep the emitted JS.
 */
import React, { useEffect, useState } from 'react';
import styled, { ThemeProvider } from 'styled-components';
import { AssetRegisterList } from '@repo/ui';
import { EquipmentCategoryList, TwinPlanner, plannerTheme } from '@repo/ui/planner';
import {
  projectRegisterRows,
  useEditorStore,
  useEquipmentCatalogStore,
  useFloorplanStore,
} from '@repo/front-end-state';
import { diagnosePlanRegisterDrift, joinRegisterOntoPlan } from '@repo/front-end-utils';
import type { FloorPlannerLayout } from '@repo/frontend-types';
import { PlanRendererProviders } from '../../components/3D/planView/PlanRendererProviders';

const Page = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
`;

const PlannerRegion = styled.div`
  position: relative;
  flex: 1 1 60%;
  min-height: 0;
`;

const RegisterRegion = styled.div`
  flex: 1 1 40%;
  min-height: 0;
  overflow: auto;
`;

/**
 * The third region, added for `plan-register-reconciliation` (FR5, AC7; epic ledger row 80).
 *
 * The page mounts no sidebar, so the **Not on plan** tag had no rig at all. `EquipmentSidebar` is
 * NOT the way to give it one: it loads the register on mount, which would break this page's
 * asserted "performs no network call and no write" property. `EquipmentCategoryList` takes no
 * props and reads only the two stores this page already seeds.
 */
const AssetsRegion = styled.div`
  flex: 0 0 22rem;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: auto;
  border-left: 1px solid #e5e7eb;
`;

const Body = styled.div`
  display: flex;
  flex: 1 1 60%;
  min-height: 0;
`;

const RegionHeading = styled.div`
  padding: 0.75rem 1rem 0.25rem;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #374151;
`;

/*
 * ── EVERYTHING BELOW LIVES INSIDE THE COMPONENT, DELIBERATELY ──────────────────────────────────
 *
 * These constants used to sit at module scope, and the code review measured what that costs: the
 * `<Route>` was correctly dead-code-eliminated by the `NODE_ENV` guard in `App.tsx`, but the
 * MODULE was not — a static import is unconditional, this app declares no `sideEffects`, and
 * Terser cannot prove a `.map()` call pure. So the twin BID, the twin id, six register row ids and
 * the customer's equipment names all shipped in the production bundle.
 *
 * Declared inside the component, they are dropped with it: nothing references the component in a
 * production build, so Terser removes the whole function and everything scoped to it. Verified the
 * same way the problem was found — build, then grep the emitted JS.
 *
 * ⚠ **The obvious fix does NOT work.** Reaching this module through a `require` inside the
 * `NODE_ENV` guard keeps it out of the graph entirely, and it also crashes the app: evaluating
 * `@repo/ui/planner` in the middle of `App`'s render threw "Rendered
 * fewer hooks than expected", and the login page rendered an empty body. Measured, then reverted.
 */
const LinkRepairFixturePage: React.FC = () => {
  const registerRows = useEquipmentCatalogStore(s => s.registerRows);
  /** Twin 532 on AdminDB Staging. Its BID is what every entity identity is resolved against. */
  const TWIN_BID = 'PUL2-COL';
  const TWIN_ID = 532;

  /**
   * Five of twin 532's 83 register rows, plus one the synthetic item can be repaired onto.
   *
   * The first five carry `entity_code: null`, which is true of **all 83** on staging — the import
   * that created them wrote no identities. The sixth is the synthetic target and is the only row
   * here that could ever be resolved by identity.
   */
  const REGISTER_ROWS = [
    { id: 9508, asset_name: '45-Degree Leg Press', entity_code: null },
    { id: 9509, asset_name: 'Low Row', entity_code: null },
    { id: 9510, asset_name: 'Hack Squat', entity_code: null },
    { id: 9511, asset_name: 'Four-Station', entity_code: null },
    { id: 9512, asset_name: 'Rear Kick', entity_code: null },
    { id: 9513, asset_name: 'Chest Press 1', entity_code: `${TWIN_BID}-E041` },
  ].map(row => ({
    id: row.id,
    twin_id: TWIN_ID,
    session_id: null,
    name: row.asset_name,
    asset_name: row.asset_name,
    entity_code: row.entity_code,
    model_3d: null,
    reference_id: null,
    manufacturer: null,
    product: null,
    product_line: null,
    match_status: 'supplied' as const,
    created_at: '2026-08-06T09:05:37.667079Z',
    created_by: null,
    modified_at: null,
    modified_by: null,
  }));

  /** The five real orphans on plan 11, plus the synthetic repairable one. */
  const PLAN_ITEMS = [
    { refid: 'r1', name: '45-Degree Leg Press', assetUid: '9427', entityCode: undefined },
    { refid: 'r2', name: 'Low Row', assetUid: '9428', entityCode: undefined },
    { refid: 'r3', name: 'Hack Squat', assetUid: '9430', entityCode: undefined },
    { refid: 'r4', name: 'Four-Station', assetUid: '9461', entityCode: undefined },
    { refid: 'r5', name: 'Rear Kick', assetUid: '9476', entityCode: undefined },
    // Synthetic: a stale link the automatic repair CAN fix, because both sides carry an identity.
    { refid: 'r6', name: 'Chest Press 1', assetUid: '9477', entityCode: `${TWIN_BID}-E041` },
  ];

  const fixtureLayout = (): FloorPlannerLayout =>
    ({
      name: 'Onboarding plan',
      floors: [
        {
          id: 1,
          name: 'Ground Floor',
          level: 0,
          height: 3,
          designs: [
            {
              name: 'Current',
              areas: [],
              walls: [],
              surfaces: [],
              cameras: [],
              observers: [],
              items: PLAN_ITEMS.map((item, i) => ({
                ...item,
                x: 2 + (i % 3) * 2.5,
                y: 2 + Math.floor(i / 3) * 2.5,
                z: 0,
                rotation: 0,
              })),
            },
          ],
        },
      ],
    }) as unknown as FloorPlannerLayout;

  const layout = useFloorplanStore(s => s.layout);
  const [seeded, setSeeded] = useState(false);

  useEffect(() => {
    /*
     * Seeded synchronously and once. `loadLayout`'s scope argument is the twin BID rather than a
     * session id, because the planner's sync hook only runs the join where the layout's recorded
     * scope matches the twin the register belongs to — seeding it with anything else would gate
     * the very behaviour this page exists to show.
     */
    /*
     * ⚠ **SEEDED ALREADY JOINED, and this is not belt-and-braces**
     * (`plan-register-reconciliation`, ruling R2).
     *
     * This page mounts `TwinPlanner` with no `equipmentPanel`, so the planner's own
     * `useRegisterPlanSync` runs with `twinId: undefined` — `resolveRegisterScope` then returns
     * null, `registerReadState` answers `no-twin`, and `repairStaleLinks` is FALSE. The synthetic
     * item `r6` therefore keeps `assetUid: '9477'` in the store and row 9513 is never claimed
     * there, even though the properties panel and the register list below both judge a repaired
     * view and show it as linked.
     *
     * That split is invisible until something READS the store's own layout, which the sidebar's
     * **Not on plan** tag does — it would mark 9513 unplaced while the register list one region
     * over counts five, not six. Seeding the joined layout makes all three regions judge one plan.
     *
     * The join is idempotent, so `joined` below and the drift breakdown are unchanged by this —
     * ASSERTED, NOT YET RE-MEASURED — this said
     * "verified by re-running `registerLinkRepair.ab.pw.spec.ts`" and it was not. That run is AC14
     * and is still outstanding; see the plan's *The A/B captures and AC14* for why. Whoever runs it
     * should know the plan measured that spec at 2 passed / 1 failed against `staging` BEFORE this
     * branch existed, so a failure there is not necessarily one this seed caused.
     */
    useFloorplanStore.getState().loadLayout(
      joinRegisterOntoPlan(fixtureLayout(), REGISTER_ROWS, TWIN_BID, {
        repairStaleLinks: true,
      }) ?? fixtureLayout(),
      TWIN_BID,
      'PUL2',
      'Onboarding plan',
    );
    useEditorStore.getState().clearHistory();
    useEditorStore.getState().clearModified();
    // Pre-selected, so the properties panel is on screen the moment the page settles. Clicking a
    // plan item from Playwright is possible but fragile, and the selection is not the thing
    // under test — what the panel offers for an already-selected stale-linked item is.
    useEditorStore.setState({ selectedEntityIds: ['r1'] });
    /*
     * All four fields, because `registerReadState` reads all four and the answer must be `ready`.
     *
     * `registerContext` in particular: without it `resolveRegisterScope` finds no twin, the read
     * state is `no-twin`, and every stale-linked item resolves to `unknown` rather than `orphan` —
     * so the panel offers no Unlink and the page shows the opposite of what it exists to show.
     * (Measured: the first capture run produced exactly that.) The token getter is never called —
     * nothing on this page writes — but the shape has to be there.
     */
    useEquipmentCatalogStore.setState({
      /*
       * ⚠ **Through the store's own projection, never a hand-rolled one.**
       * `registerRows` is the store's single source and `siteAssets` / `assetLookup` / `grouped` /
       * `filteredGrouped` are derived from it ONLY inside `projectRegisterRows` — so setting
       * `registerRows` alone (which this page did before it grew a sidebar region) leaves the
       * accordion with nothing to render. Rebuilding the projection here would be exactly the
       * drift that function exists as one place to prevent.
       */
      ...projectRegisterRows(REGISTER_ROWS, ''),
      loadedKey: `twin:${TWIN_ID}`,
      isLoading: false,
      error: null,
      registerContext: {
        twinId: TWIN_ID,
        baseUrl: 'http://localhost:3000/api/',
        getAccessToken: async () => '',
      },
    });
    setSeeded(true);
    return () => {
      useFloorplanStore.getState().reset();
      useEquipmentCatalogStore.getState().clear();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs once on mount: seeds the stores from fixed fixture data
  }, []);

  // The same two calls the wizard's Equipment step makes, so the breakdown on screen is the one an
  // operator would really see: diagnose the JOINED layout, with the repair enabled.
  const joined = joinRegisterOntoPlan(layout, registerRows, TWIN_BID, { repairStaleLinks: true });
  const drift = seeded ? diagnosePlanRegisterDrift(joined, registerRows, TWIN_BID) : null;

  return (
    <PlanRendererProviders twinRecord={null} twinBid={TWIN_BID}>
      {/* Renderer-switch R3.2: this fixture draws in 3D, as onboarding does. No twin record: the
          anchor falls back as onboarding's does with none. */}
      <Page>
        <Body>
          <PlannerRegion data-testid="fixture-planner">
            <ThemeProvider theme={plannerTheme}>
              <TwinPlanner showToolbar={false} showFullscreenButton={false} twinBid={TWIN_BID} />
            </ThemeProvider>
          </PlannerRegion>
          {/*
           * The **Your Assets** region (`plan-register-reconciliation`, FR5/AC7).
           *
           * Mounted OUTSIDE `TwinPlanner`, so no `PlannerLayoutContext` reaches it and
           * `usePlanPlacementState` falls back to the floorplan store — which is why the store is
           * seeded with the joined layout above. Its own `ThemeProvider`, because the accordion and
           * the cards are Planner internals and read `plannerTheme`.
           */}
          <AssetsRegion data-testid="fixture-assets">
            <ThemeProvider theme={plannerTheme}>
              <RegionHeading>Your Assets</RegionHeading>
              <EquipmentCategoryList />
            </ThemeProvider>
          </AssetsRegion>
        </Body>
        <RegisterRegion data-testid="fixture-register">
          {/*
           * No `twinBid` prop — `AssetRegisterList` takes none. It reads the twin from the layout
           * and the rows it is given. CI caught this; a local type check could not, because local
           * module resolution was widening these props to `any`.
           */}
          <AssetRegisterList assets={registerRows} layout={joined ?? null} drift={drift} />
        </RegisterRegion>
      </Page>
    </PlanRendererProviders>
  );
};

export default LinkRepairFixturePage;
